/**
 * ConfirmProvider + useConfirm — imperative confirm() API.
 * Replaces window.confirm() with a styled ConfirmDialog.
 * @layer atom
 * @package @plerity/ui-core
 */

import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useRef, useState } from "react";
import { ConfirmDialog } from "./ConfirmDialog";
import type { ConfirmOptions } from "./ConfirmDialog.types";

// ── context ───────────────────────────────────────────────────────────────────

interface ConfirmContextValue {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

// ── provider ──────────────────────────────────────────────────────────────────

interface DialogState {
  open: boolean;
  opts: ConfirmOptions;
  loading: boolean;
}

const INITIAL_STATE: DialogState = {
  open: false,
  opts: { title: "" },
  loading: false,
};

export interface ConfirmProviderProps {
  children: ReactNode;
}

export function ConfirmProvider({ children }: ConfirmProviderProps) {
  const [state, setState] = useState<DialogState>(INITIAL_STATE);
  // Store the resolve function from the pending Promise
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
      setState({ open: true, opts, loading: false });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    resolveRef.current?.(true);
    resolveRef.current = null;
    setState(INITIAL_STATE);
  }, []);

  const handleCancel = useCallback(() => {
    resolveRef.current?.(false);
    resolveRef.current = null;
    setState(INITIAL_STATE);
  }, []);

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      <ConfirmDialog
        open={state.open}
        title={state.opts.title}
        message={state.opts.message}
        confirmLabel={state.opts.confirmLabel}
        cancelLabel={state.opts.cancelLabel}
        variant={state.opts.variant}
        loading={state.loading}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </ConfirmContext.Provider>
  );
}

ConfirmProvider.displayName = "ConfirmProvider";

// ── hook ──────────────────────────────────────────────────────────────────────

/**
 * Returns an imperative `confirm(opts)` function that shows a ConfirmDialog
 * and resolves to `true` (confirmed) or `false` (cancelled).
 *
 * Must be used inside a `<ConfirmProvider>`.
 */
export function useConfirm(): (opts: ConfirmOptions) => Promise<boolean> {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm must be used inside a <ConfirmProvider>");
  }
  return ctx.confirm;
}
